import { useState } from 'react';

import { DialogNugget, NuggetUXVariant, PanelNugget } from '@backrs_ui/common-ui';

import { handleBookmark } from '@backrs_ui/grow-ui/src/components/Nuggets/nugget.util';
import { LubavResource } from '@backrs_ui/grow-ui/src/models/models';
import { useAppDispatch, useAppSelector } from '@backrs_ui/grow-ui/src/redux/hooks';
import { selectBookmarkedNuggetId } from '@backrs_ui/grow-ui/src/redux/user/user';

import { Box, Dialog } from '@mui/material';

import { commonStyles } from './CommonStyles';

const PROSPER_URL = process.env.REACT_APP_GROW_URL;

type ResourcesFormatterProps = Readonly<{
  resources: LubavResource[];
}>;

function getUXVariant(resource: LubavResource): NuggetUXVariant {
  if (!resource || !resource.metadata) {
    return { type: 'resource', title: resource?.metadata?.prosper_data?.title || '' };
  }

  switch (resource.metadata?.prosper_data?.variant) {
    case 'article':
      return {
        type: 'article',
        articleUrl: resource.metadata?.prosper_data?.external_url,
        title: resource.metadata?.prosper_data?.title,
        previewImage: resource.metadata?.prosper_data?.preview_media?.media_url,
      };
    case 'opportunity':
      return {
        type: 'opportunity',
        deadline: resource.metadata?.prosper_data?.end_date || '',
        mediaUrl: resource.metadata?.prosper_data?.media?.media_url,
      };
    case 'event':
      return {
        type: 'event',
        date: resource.metadata?.prosper_data?.end_date || '',
      };
    case 'video-internal':
      return {
        type: 'video-internal',
        url: resource.metadata?.prosper_data?.media?.media_url || '',
        previewImage: resource.metadata?.prosper_data?.preview_media?.media_url,
      };
    case 'video-external':
      return {
        type: 'video-external',
        url: resource.metadata?.prosper_data?.external_url || '',
        previewImage: resource.metadata?.prosper_data?.preview_media?.media_url,
      };
    default:
      return {
        type: 'resource',
        title: resource.metadata?.prosper_data?.title,
        previewImage: resource.metadata?.prosper_data?.preview_media?.media_url,
      };
  }
}

export const ResourcesFormatter = ({ resources }: ResourcesFormatterProps) => {
  const [open, setOpen] = useState(false);
  const [activeDialogResourceNugget, setActiveDialogResourceNugget] =
    useState<LubavResource | null>(null);

  const bookmarkId = useAppSelector(
    selectBookmarkedNuggetId({
      nuggetId: activeDialogResourceNugget?.metadata?.prosper_data.id,
      variant: activeDialogResourceNugget?.metadata?.prosper_data.variant,
    }),
  );

  const dispatch = useAppDispatch();

  const dialogNugget = activeDialogResourceNugget ? (
    <Dialog open={open} onClose={() => setOpen(false)} maxWidth="md">
      <DialogNugget
        shareParams={
          PROSPER_URL
            ? {
                title: `Prosper: ${activeDialogResourceNugget.metadata?.prosper_data?.title}`,
                text: `Prosper: ${activeDialogResourceNugget.metadata?.prosper_data?.description}`,
                url: `${PROSPER_URL}?nugget=${activeDialogResourceNugget.metadata?.prosper_data?.slug}`,
              }
            : undefined
        }
        title={activeDialogResourceNugget?.metadata?.prosper_data?.title || ''}
        variant={getUXVariant(activeDialogResourceNugget)}
        description={activeDialogResourceNugget?.metadata?.prosper_data?.description || ''}
        columnCategory={
          activeDialogResourceNugget?.metadata?.prosper_data?.content_nugget_theme || 'Learn'
        }
        saveToLibrary={() =>
          activeDialogResourceNugget?.metadata?.prosper_data
            ? handleBookmark({
                nuggetId: activeDialogResourceNugget?.metadata?.prosper_data.id,
                dispatch,
                bookmarkId,
                variant: activeDialogResourceNugget.metadata?.prosper_data.variant,
              })
            : null
        }
        hasBookmarkedNugget={!!bookmarkId}
      />
    </Dialog>
  ) : null;

  return (
    <>
      {dialogNugget}
      <Box sx={commonStyles.nuggetList}>
        {resources?.map((resource) => {
          const variant: NuggetUXVariant = getUXVariant(resource);

          return (
            <PanelNugget
              key={resource.id}
              title={resource.metadata?.prosper_data?.title || ''}
              body={resource.metadata?.prosper_data?.description}
              size="SMALL"
              variant={variant}
              subText={resource.relevance}
              onClick={() => {
                setActiveDialogResourceNugget(resource);
                setOpen(true);
              }}
            />
          );
        })}
      </Box>
    </>
  );
};
